import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Plus,
  Target,
  CalendarRange,
  Users,
  MessageSquarePlus,
  Eye,
  Tag,
  X,
  Search,
  Info,
} from 'lucide-react'
import { api, extractApiError, isPlanLimitError, isSessionEndedError } from '../../lib/api'
import { lengthError } from '../../lib/fieldLength'
import { goalDetail, goalAssignableOwnersResponse } from '../../lib/types'
import { useAuthStore } from '../../stores/authStore'
import { onSocketEvent } from '../../lib/socket'
import { useRedirectOnNotFound } from '../../lib/useRedirectOnNotFound'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { GoalFormModal, emptyGoalForm, goalToForm } from '../../components/GoalFormModal'
import {
  PRIORITY_LABEL,
  HEALTH_LABEL,
  HEALTH_PILL,
  STATUS_OPTIONS,
  TARGET_LABEL,
  formatKrValue,
  fmtDate,
  ProgressRing,
} from '../../lib/goals-ui.jsx'
// Canonical app-wide date-time formatter (DD/MM/YYYY HH:MM AM/PM, display
// timezone). Used for progress-update timestamps so they match the rest of the
// app instead of the old bespoke 24-hour/comma/browser-local format.
import { formatDateTime } from '../../lib/format'
import { Avatar } from '../../components/Avatar'
import { AvatarStack } from '../../components/AvatarStack'

const TYPE_META = {
  weekly: { label: 'Weekly', icon: Target },
  monthly: { label: 'Monthly', icon: CalendarRange },
}

export function GoalDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.user)
  const [editOpen, setEditOpen] = useState(false)
  const [addChildOpen, setAddChildOpen] = useState(false)
  const [deleteGoalOpen, setDeleteGoalOpen] = useState(false)
  const [removeChild, setRemoveChild] = useState(null)
  const [peopleOpen, setPeopleOpen] = useState(false)
  const [note, setNote] = useState('')
  const [noteProgress, setNoteProgress] = useState('')
  // Inline validation for the Note field. Shown BELOW the field (like every
  // other form in the app + the QA standard) rather than as a toast.
  const [noteError, setNoteError] = useState('')
  // Inline validation for the optional Progress field (custom, not the browser's
  // native number-input bubble — the form is noValidate).
  const [progressError, setProgressError] = useState('')
  // Which progress update's "viewed by" list is open (id) — the eye control.
  const [viewersOpenId, setViewersOpenId] = useState(null)

  const goalKey = ['goal', id]
  const goalQuery = useQuery({
    queryKey: goalKey,
    queryFn: async () => goalDetail.parse((await api.get(`/goals/${id}`)).data),
  })
  const goal = goalQuery.data

  // If ANOTHER user deletes this goal while it's open, don't strand the viewer
  // on a bare "Goal not found." — mirror what the deleter sees: a toast + a
  // bounce back to the Goals list. Listens to the same realtime `goal` event the
  // backend emits on delete (op: 'deleted'). The deleter is already redirected by
  // the delete mutation's onSuccess, so we skip them via the actorId guard.
  useEffect(() => {
    const off = onSocketEvent('data:changed', (payload) => {
      if (
        payload?.resource === 'goal' &&
        payload?.op === 'deleted' &&
        String(payload.id) === String(id) &&
        String(payload.actorId ?? '') !== String(me?.id ?? '')
      ) {
        toast('This goal has been deleted.')
        navigate('/app/goals')
      }
    })
    return off
  }, [id, me?.id, navigate])

  // Direct navigation to an already-deleted (or inaccessible) goal 404s — don't
  // strand the user on the "Goal not found." page; bounce to the Goals list with
  // a notice. (The realtime listener above handles the live "deleted while
  // viewing" case for a snappier redirect with no error flash.)
  useRedirectOnNotFound(goalQuery, () => navigate('/app/goals'), 'This goal is no longer available.')
  // A follower who was just removed from the goal (the owner unfollowed them)
  // loses read access, so GET /goals/:id starts returning 403 ("You do not have
  // access to this goal."). Treat that like the 404 case — bounce to the Goals
  // list with a friendly notice instead of stranding them on a raw access-denied
  // message.
  useRedirectOnNotFound(
    goalQuery,
    () => navigate('/app/goals'),
    'You no longer have access to this goal.',
    [403],
  )

  const isOwner = goal && me && String(goal.ownerId) === String(me.id)
  // Server-computed permission flags (owner OR creator OR admin OR a supervisor
  // of the owner). Drive the Edit / Delete / Manage / Reassign controls; the API
  // re-enforces authz on every mutation. Fall back to owner-only pre-load.
  const canManage = goal?.canManage ?? isOwner
  // `canEdit` is the definition-level right (creator / admin / supervisor) that
  // gates Edit, Delete, sub-goals and managing people — an assigned owner who
  // isn't the creator keeps `canManage` (status/progress) but not `canEdit`.
  const canEdit = goal?.canEdit ?? false
  const canReassign = goal?.canReassign ?? false
  // The viewer had this goal reassigned away from them recently and may take it
  // back (revert to self) — a correction path that needs no assign capability.
  const canRevertReassign = goal?.canRevertReassign ?? false
  const TypeIcon = goal ? (TYPE_META[goal.type]?.icon ?? Target) : Target

  const refresh = () => {
    qc.invalidateQueries({ queryKey: goalKey })
    qc.invalidateQueries({ queryKey: ['goals'] })
  }

  const updateMutation = useMutation({
    mutationFn: async (patch) => (await api.patch(`/goals/${id}`, patch)).data,
    onSuccess: () => refresh(),
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })
  // Key-result progress can be moved by the owner OR any follower (member) — a
  // scoped update that only changes a key result's `current` value.
  const progressMutation = useMutation({
    mutationFn: async ({ id: krId, current }) =>
      (await api.patch(`/goals/${id}/progress`, { keyResults: [{ id: krId, current }] })).data,
    onSuccess: () => refresh(),
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })
  const createChildMutation = useMutation({
    mutationFn: async (payload) =>
      (await api.post('/goals', { type: goal.type, parentGoalId: id, ...payload })).data,
    onSuccess: () => {
      refresh()
      toast.success('Sub-goal added.')
      setAddChildOpen(false)
    },
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })
  const deleteChildMutation = useMutation({
    mutationFn: async (childId) => api.delete(`/goals/${childId}`),
    onSuccess: () => {
      refresh()
      toast.success('Sub-goal deleted successfully.')
      setRemoveChild(null)
    },
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })
  const deleteGoalMutation = useMutation({
    mutationFn: async () => api.delete(`/goals/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goals'] })
      toast.success('Goal deleted successfully.')
      navigate(`/app/goals?tab=${goal?.type ?? 'weekly'}`)
    },
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })
  const addUpdateMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/goals/${id}/updates`, {
        note: note.trim(),
        progress: noteProgress === '' ? null : Number(noteProgress),
      })).data,
    onSuccess: () => {
      refresh()
      setNote('')
      setNoteProgress('')
      setProgressError('')
      toast.success('Progress update posted.')
    },
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })
  const delUpdateMutation = useMutation({
    mutationFn: async (updateId) => api.delete(`/goals/${id}/updates/${updateId}`),
    onSuccess: () => refresh(),
    onError: (err) => {
      // The API interceptor already surfaces ONE amber "upgrade your plan"
      // warning (and its own dead-session toast), so suppress our duplicate.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  if (goalQuery.isLoading) {
    return (
      <div>
        <Breadcrumb current="Goal" homePath="/app" homeLabel="Dashboard" />
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading goal…</p>
      </div>
    )
  }
  if (goalQuery.isError || !goal) {
    // A 404 (deleted / stale URL) or 403 (this user just lost access — e.g. a
    // removed follower) both trigger the redirect-to-Goals effects above, so
    // show a neutral "redirecting" line instead of flashing the raw
    // not-found / access-denied message before the bounce. Any OTHER error
    // (e.g. a 500) still surfaces its message + a manual escape link.
    const errStatus =
      goalQuery.error?.response?.status ?? goalQuery.error?.status
    const redirecting = errStatus === 403 || errStatus === 404
    return (
      <div>
        <Breadcrumb current="Goal" homePath="/app" homeLabel="Dashboard" />
        {redirecting ? (
          <p className="text-sm text-[color:var(--color-ink-muted)]">
            Taking you back to Goals…
          </p>
        ) : (
          <>
            <p className="text-sm text-[color:var(--color-priority-urgent)]">
              {goalQuery.error ? extractApiError(goalQuery.error).message : 'Goal not found.'}
            </p>
            <Link to="/app/goals" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--color-accent-strong)] hover:underline">
              <ArrowLeft size={15} /> Back to Goals
            </Link>
          </>
        )}
      </div>
    )
  }

  const children = goal.children ?? []
  const canPostUpdate =
    canManage || (goal.followers ?? []).some((f) => String(f.id) === String(me?.id))

  return (
    <div>
      <Breadcrumb current={goal.name} homePath="/app" homeLabel="Dashboard" />

      <Link
        to={`/app/goals?tab=${goal.type}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]"
      >
        <ArrowLeft size={15} /> Back to {TYPE_META[goal.type]?.label ?? ''} Goals
      </Link>

      {/* Hero header — navy, matching the sidebar chrome (.brand-navy-surface). */}
      <div className="brand-navy-surface overflow-hidden rounded-2xl">
        <div className="flex flex-wrap items-center justify-between gap-4 p-6">
          <div className="flex min-w-0 items-center gap-4">
            <ProgressRing
              percent={goal.progress != null ? goal.progress : goal.completed ? 100 : 0}
              size={80}
              stroke={8}
              barColor="#ffffff"
              trackColor="rgba(255,255,255,0.28)"
              textClass="text-white"
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="min-w-0 break-words [overflow-wrap:anywhere] font-display text-2xl text-white">{goal.name}</h1>
                <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[12px] font-semibold text-white">
                  <TypeIcon size={12} />
                  {TYPE_META[goal.type]?.label}
                </span>
                <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                  {PRIORITY_LABEL[goal.priority] ?? 'Normal'}
                </span>
                {goal.health ? (
                  <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    {HEALTH_LABEL[goal.health] ?? goal.health}
                  </span>
                ) : null}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/85">
                <span>{fmtDate(goal.startDate)} – {fmtDate(goal.targetDate)}</span>
                <span className="inline-flex items-center gap-1.5">
                  <Avatar name={goal.ownerName ?? ''} avatarUrl={goal.ownerAvatarUrl ?? null} size="xs" colorByName />
                  {goal.ownerName ?? '—'}
                </span>
                {goal.createdById && String(goal.createdById) !== String(goal.ownerId) ? (
                  <span>Created by {goal.createdByName ?? '—'}</span>
                ) : null}
              </div>
            </div>
          </div>
          {canEdit ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/30 cursor-pointer"
              >
                <Pencil size={15} /> Edit
              </button>
              <button
                type="button"
                onClick={() => setDeleteGoalOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/30 cursor-pointer"
              >
                <Trash2 size={15} /> Delete
              </button>
            </div>
          ) : null}
        </div>
        {goal.description ? (
          <div className="bg-[color:var(--color-card)] px-6 py-4">
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm text-[color:var(--color-ink)]">{goal.description}</p>
          </div>
        ) : null}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Left column: key results + sub-goals */}
        <div className="space-y-5 lg:col-span-2">
          {/* Key results */}
          {goal.keyResults?.length ? (
            <Section
              title="Key results"
              info="Measurable outcomes that track this goal. Each key result runs from a start value to a target — update its current value to move the bar and the goal's overall progress."
            >
              <div className="space-y-2">
                {goal.keyResults.map((t) => (
                  <div key={t.id} className="flex items-center gap-2">
                    {/* `title` feeds the global hover tooltip so a truncated
                     *  Key Result name is readable in full without editing. */}
                    <span
                      title={t.name || TARGET_LABEL[t.type]}
                      className="w-40 shrink-0 truncate text-xs text-[color:var(--color-ink-muted)]"
                    >
                      {t.name || TARGET_LABEL[t.type]}
                    </span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-[color:var(--color-card-hover)]">
                      <span className="block h-full rounded-full bg-[color:var(--color-accent-strong)]" style={{ width: `${t.progress ?? 0}%` }} />
                    </span>
                    {canPostUpdate ? (
                      // Members (owner + followers) can move progress inline.
                      <KrProgressInput
                        kr={t}
                        saving={progressMutation.isPending}
                        onSave={(current) => progressMutation.mutate({ id: t.id, current })}
                      />
                    ) : (
                      <span className="min-w-[8rem] shrink-0 whitespace-nowrap text-right text-xs tabular-nums text-[color:var(--color-ink-muted)]">
                        {formatKrValue(t)}
                      </span>
                    )}
                    {/* Actual progress % — start-adjusted, so it matches the
                     *  goal ring. Shown next to the raw value because with a
                     *  non-zero start "90 / 100" is NOT the same as the percent
                     *  (e.g. start 20 → 90/100 reads as 90% but is 88%). */}
                    <span
                      title="Progress"
                      className="w-11 shrink-0 whitespace-nowrap text-right text-xs font-semibold tabular-nums text-[color:var(--color-ink-strong)]"
                    >
                      {t.progress ?? 0}%
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          ) : null}

          {/* Sub-goals */}
          <Section
            title="Sub-goals"
            action={
              canEdit ? (
                <button
                  type="button"
                  onClick={() => setAddChildOpen(true)}
                  className="inline-flex items-center gap-1 rounded-lg border border-[color:var(--color-rule)] px-2 py-1 text-xs text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  <Plus size={13} /> Add Sub-goal
                </button>
              ) : null
            }
          >
            {children.length === 0 ? (
              <p className="text-sm text-[color:var(--color-ink-muted)]">
                No sub-goals. Break this goal down into smaller measurable pieces — their progress rolls up here.
              </p>
            ) : (
              <ul className="space-y-2">
                {children.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 rounded-lg border border-[color:var(--color-rule)] p-2.5">
                    {c.progress != null ? (
                      <ProgressRing percent={c.progress} size={34} stroke={4} />
                    ) : (
                      <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-[color:var(--color-rule)] text-[10px] text-[color:var(--color-ink-subtle)]">
                        —
                      </span>
                    )}
                    <div className="min-w-0 flex-1 break-words">
                      <Link to={`/app/goals/${c.id}`} className="font-medium text-[color:var(--color-ink-strong)] hover:text-[color:var(--color-accent-strong)] hover:underline">
                        {c.name}
                      </Link>
                      {c.health ? (
                        <span className={'ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ' + (HEALTH_PILL[c.health] ?? '')}>
                          {HEALTH_LABEL[c.health] ?? c.health}
                        </span>
                      ) : null}
                    </div>
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => setRemoveChild(c)}
                        aria-label={`Delete ${c.name}`}
                        className="rounded p-1.5 text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-priority-urgent)] cursor-pointer"
                      >
                        <Trash2 size={14} />
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        {/* Right column: status + people + progress updates */}
        <div className="space-y-5">
          {/* Status — quick inline update, no edit form needed. Editable by
           *  whoever may manage the goal; read-only for everyone else. */}
          <Section title="Status">
            {canManage ? (
              <select
                value={goal.manualStatus ?? 'not_started'}
                disabled={updateMutation.isPending}
                onChange={(e) =>
                  updateMutation.mutate(
                    { manualStatus: e.target.value },
                    { onSuccess: () => toast.success('Status updated.') },
                  )
                }
                aria-label="Goal status"
                className="w-full cursor-pointer rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2.5 py-1.5 text-sm text-[color:var(--color-ink)] focus:border-[color:var(--color-accent)] focus:outline-none"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-[color:var(--color-ink)]">
                {STATUS_OPTIONS.find((o) => o.value === (goal.manualStatus ?? 'not_started'))
                  ?.label ?? 'Not Started'}
              </p>
            )}
          </Section>

          {/* People (owner + followers) */}
          <Section
            title="People"
            action={
              canEdit ? (
                <button
                  type="button"
                  onClick={() => setPeopleOpen(true)}
                  className="inline-flex items-center gap-1 rounded-lg border border-[color:var(--color-rule)] px-2 py-1 text-xs text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  <Users size={13} /> Manage
                </button>
              ) : null
            }
          >
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wide text-[color:var(--color-ink-subtle)]">Assignee</dt>
                <dd className="mt-1 flex items-center gap-2 text-[color:var(--color-ink)]">
                  <Avatar
                    name={goal.ownerName ?? ''}
                    avatarUrl={goal.ownerAvatarUrl ?? null}
                    size="sm"
                    colorByName
                    tooltip={false}
                  />
                  <span className="min-w-0 truncate">{goal.ownerName ?? '—'}</span>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-[color:var(--color-ink-subtle)]">Followers</dt>
                <dd className="mt-1 flex flex-wrap gap-1.5">
                  {(goal.followers ?? []).length === 0 ? (
                    <span className="text-[color:var(--color-ink-muted)]">None</span>
                  ) : (
                    // Profile avatars (photo or initials) with name-on-hover
                    // tooltips — same presentation as the assignee avatar stack.
                    <AvatarStack people={goal.followers} size={28} />
                  )}
                </dd>
              </div>
            </dl>
          </Section>

          {/* Tags */}
          {(goal.tags ?? []).length > 0 ? (
            <Section title="Tags">
              <div className="flex flex-wrap gap-1.5">
                {goal.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-accent-bg)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-accent-strong)]"
                  >
                    <Tag size={11} />
                    {tag}
                  </span>
                ))}
              </div>
            </Section>
          ) : null}

          {/* Progress Updates */}
          <Section title="Progress Updates">
            {canPostUpdate ? (
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault()
                  if (note.trim().length === 0) {
                    setNoteError('Enter a progress update note before posting.')
                    return
                  }
                  const noteLenErr = lengthError(note, undefined, 2000, 'Progress update note')
                  if (noteLenErr) {
                    setNoteError(noteLenErr)
                    return
                  }
                  setNoteError('')
                  // Custom validation for the optional Progress field (0–100),
                  // replacing the browser's native number-input message.
                  const raw = noteProgress.trim()
                  if (raw !== '') {
                    const n = Number(raw)
                    if (!Number.isFinite(n) || n < 0 || n > 100) {
                      setProgressError('Progress must be between 0 and 100.')
                      return
                    }
                  }
                  setProgressError('')
                  addUpdateMutation.mutate()
                }}
                className="mb-3 space-y-3"
              >
                <div className="space-y-1">
                  <label htmlFor="checkin-note" className="block w-fit text-xs font-medium text-[color:var(--color-ink-muted)]">
                    Note <span className="text-[color:var(--color-priority-urgent)]">*</span>
                  </label>
                  <textarea
                    id="checkin-note"
                    value={note}
                    onChange={(e) => {
                      setNote(e.target.value)
                      if (noteError) setNoteError('')
                    }}
                    placeholder="Enter a Progress Update Note"
                    rows={2}
                    /* Soft limit: accept input past 2000 and flag it (below) —
                       the server DTO (max 2000) blocks the actual save. */
                    // Bound the drag-resize range (min ~2 rows, max ~10rem) via
                    // inline style so the limit doesn't depend on Tailwind height
                    // utilities being generated (QA: message fields have a bounded
                    // expansion size). `resize` respects these; it scrolls past the cap.
                    style={{ minHeight: '4.5rem', maxHeight: '10rem' }}
                    aria-invalid={noteError ? true : undefined}
                    aria-describedby={noteError ? 'checkin-note-error' : undefined}
                    className={
                      // `resize-y` = vertical drag only (no horizontal, which would
                      // break the narrow column). Min/max drag bounds are the inline
                      // `style` above.
                      'w-full resize-y rounded-lg border bg-[color:var(--color-canvas)] px-3 py-2 text-sm text-[color:var(--color-ink)] focus:outline-none ' +
                      (noteError
                        ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                        : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
                    }
                  />
                  {noteError || lengthError(note, undefined, 2000, 'Progress update note') ? (
                    <p
                      id="checkin-note-error"
                      role="alert"
                      className="text-xs text-[color:var(--color-priority-urgent)]"
                    >
                      {noteError || lengthError(note, undefined, 2000, 'Progress update note')}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-end justify-between gap-3">
                  <div className="space-y-1">
                    <label htmlFor="checkin-progress" className="block w-fit text-xs font-medium text-[color:var(--color-ink-muted)]">
                      Progress (optional)
                    </label>
                    <div className="relative w-32">
                      <input
                        id="checkin-progress"
                        type="number"
                        value={noteProgress}
                        onChange={(e) => {
                          setNoteProgress(e.target.value)
                          if (progressError) setProgressError('')
                        }}
                        placeholder="0"
                        aria-invalid={progressError ? true : undefined}
                        aria-describedby={progressError ? 'checkin-progress-error' : undefined}
                        className={
                          'h-9 w-full rounded-md border bg-[color:var(--color-canvas)] pl-3 pr-7 text-sm text-[color:var(--color-ink)] focus:outline-none ' +
                          (progressError
                            ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                            : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
                        }
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[color:var(--color-ink-subtle)]">%</span>
                    </div>
                    {progressError ? (
                      <p
                        id="checkin-progress-error"
                        role="alert"
                        className="text-xs text-[color:var(--color-priority-urgent)]"
                      >
                        {progressError}
                      </p>
                    ) : null}
                  </div>
                  <Button type="submit" className="h-9" loading={addUpdateMutation.isPending}>
                    <MessageSquarePlus size={15} /> Post
                  </Button>
                </div>
              </form>
            ) : null}
            {(goal.updates ?? []).length === 0 ? (
              <p className="text-sm text-[color:var(--color-ink-muted)]">No progress updates yet.</p>
            ) : (
              <ul className="space-y-3">
                {goal.updates.map((u) => {
                  const canDel = isOwner || String(u.byId ?? '') === String(me?.id)
                  return (
                    <li key={u.id} className="rounded-lg border border-[color:var(--color-rule)] p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-[color:var(--color-ink-strong)]">{u.byName ?? 'Someone'}</span>
                        <div className="flex shrink-0 items-center gap-2">
                          {u.progress != null ? (
                            <span className="rounded-full bg-[color:var(--color-accent-bg)] px-1.5 py-0.5 text-[10px] font-semibold text-[color:var(--color-accent-strong)]">
                              {u.progress}%
                            </span>
                          ) : null}
                          {u.viewCount > 0 ? (
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() =>
                                  setViewersOpenId((cur) => (cur === u.id ? null : u.id))
                                }
                                aria-haspopup="true"
                                aria-expanded={viewersOpenId === u.id}
                                title="See who viewed this update"
                                className="inline-flex items-center gap-1 rounded text-[11px] text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink)] cursor-pointer"
                              >
                                <Eye size={12} />
                                {u.viewCount}
                              </button>
                              {viewersOpenId === u.id ? (
                                <>
                                  <button
                                    type="button"
                                    aria-hidden="true"
                                    tabIndex={-1}
                                    onClick={() => setViewersOpenId(null)}
                                    className="fixed inset-0 z-40 cursor-default"
                                  />
                                  <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-2 shadow-lg">
                                    <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-ink-subtle)]">
                                      Viewed by
                                    </p>
                                    <ul className="max-h-48 space-y-1 overflow-y-auto">
                                      {(u.viewers ?? []).length ? (
                                        u.viewers.map((v) => (
                                          <li key={v.id} className="flex items-center gap-2 px-1 py-0.5">
                                            <Avatar name={v.name ?? ''} avatarUrl={v.avatarUrl ?? null} size="xs" colorByName tooltip={false} />
                                            <span className="truncate text-xs text-[color:var(--color-ink)]">{v.name ?? 'Someone'}</span>
                                          </li>
                                        ))
                                      ) : (
                                        <li className="px-1 py-0.5 text-xs text-[color:var(--color-ink-muted)]">No viewers yet.</li>
                                      )}
                                    </ul>
                                  </div>
                                </>
                              ) : null}
                            </div>
                          ) : null}
                          <span className="text-[11px] text-[color:var(--color-ink-subtle)]">{formatDateTime(u.at)}</span>
                          {canDel ? (
                            <button
                              type="button"
                              onClick={() => delUpdateMutation.mutate(u.id)}
                              aria-label="Delete progress update"
                              className="rounded p-0.5 text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] cursor-pointer"
                            >
                              <X size={13} />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm text-[color:var(--color-ink)]">{u.note}</p>
                    </li>
                  )
                })}
              </ul>
            )}
          </Section>
        </div>
      </div>

      {/* Edit goal */}
      <GoalFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={(payload) =>
          updateMutation.mutate(payload, {
            onSuccess: () => {
              refresh()
              toast.success('Goal updated.')
              setEditOpen(false)
            },
          })
        }
        saving={updateMutation.isPending}
        title="Edit Goal"
        submitLabel="Save Goal"
        isEdit
        type={goal.type}
        initial={goalToForm(goal)}
      />

      {/* Add sub-goal */}
      <GoalFormModal
        open={addChildOpen}
        onClose={() => setAddChildOpen(false)}
        onSubmit={(payload) => createChildMutation.mutate(payload)}
        saving={createChildMutation.isPending}
        title="Add Sub-goal"
        submitLabel="Add Sub-goal"
        type={goal.type}
        initial={emptyGoalForm({ startDate: goal.startDate, targetDate: goal.targetDate })}
      />

      {canEdit ? (
        <ManagePeopleModal
          open={peopleOpen}
          onClose={() => setPeopleOpen(false)}
          goal={goal}
          canReassign={canReassign}
          canRevertReassign={canRevertReassign}
          currentUserId={me?.id}
          saving={updateMutation.isPending}
          onSave={(patch) =>
            updateMutation.mutate(patch, {
              onSuccess: () => {
                refresh()
                toast.success(
                  patch.ownerId === undefined
                    ? 'Followers updated.'
                    : String(patch.ownerId) === String(me?.id)
                      ? 'Assignment restored to you.'
                      : 'Assignee reassigned.',
                )
                setPeopleOpen(false)
              },
            })
          }
        />
      ) : null}

      <ConfirmDialog
        open={deleteGoalOpen}
        title="Delete Goal?"
        message={`"${goal.name}" will be deleted. Any sub-goals are promoted to this goal's parent.`}
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteGoalMutation.isPending}
        onCancel={() => setDeleteGoalOpen(false)}
        onConfirm={() => deleteGoalMutation.mutate()}
      />
      <ConfirmDialog
        open={Boolean(removeChild)}
        title="Delete Sub-goal?"
        message={removeChild ? `"${removeChild.name}" will be removed.` : ''}
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteChildMutation.isPending}
        onCancel={() => setRemoveChild(null)}
        onConfirm={() => removeChild && deleteChildMutation.mutate(removeChild.id)}
      />
    </div>
  )
}

function Section({ title, action, info, children }) {
  return (
    <section className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-[color:var(--color-ink-strong)]">
          {title}
          {/* Optional info hint — `title` feeds the app-wide hover tooltip so the
           *  section's purpose is one hover away without cluttering the header. */}
          {info ? (
            <span
              title={info}
              aria-label={info}
              tabIndex={0}
              className="inline-flex shrink-0 cursor-help text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink-muted)] focus:outline-none focus-visible:text-[color:var(--color-accent-strong)]"
            >
              <Info size={14} aria-hidden="true" />
            </span>
          ) : null}
        </h2>
        {action}
      </div>
      {children}
    </section>
  )
}

/** Inline editable CURRENT value for a key result — shown to members (owner +
 *  followers) so they can move the goal's progress. Saves on blur / Enter, and
 *  only when the value actually changed. */
function KrProgressInput({ kr, onSave, saving }) {
  const [val, setVal] = useState(kr.current ?? '')
  // Re-sync after a save (or a realtime change) updates the stored value.
  useEffect(() => {
    setVal(kr.current ?? '')
  }, [kr.current])
  const suffix = kr.type === 'percentage' ? '%' : ''
  // Valid range for the current value: 0 … the further of start/target (so an
  // ascending 0→100 or descending 100→0 key result both cap correctly). Progress
  // can't go below 0 or beyond that ceiling.
  const start = Number(kr.start ?? 0)
  const targetNum = kr.target == null ? null : Number(kr.target)
  const max = targetNum == null || !Number.isFinite(targetNum) ? null : Math.max(start, targetNum)
  const commit = () => {
    const trimmed = String(val).trim()
    const parsed = trimmed === '' ? null : Number(trimmed)
    if (parsed !== null && !Number.isFinite(parsed)) {
      setVal(kr.current ?? '')
      return
    }
    let next = parsed
    if (next !== null) {
      if (next < 0) {
        next = 0
        toast.error('Progress cannot be negative.')
      } else if (max != null && next > max) {
        next = max
        toast.error(`Progress cannot exceed the target of ${max}.`)
      }
      if (next !== parsed) setVal(String(next))
    }
    if (next === (kr.current ?? null)) return
    onSave(next)
  }
  return (
    <span className="flex min-w-[8rem] shrink-0 items-center justify-end gap-1 whitespace-nowrap text-xs tabular-nums text-[color:var(--color-ink-muted)]">
      <input
        type="number"
        min="0"
        max={max ?? undefined}
        value={val}
        disabled={saving}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        aria-label="Current value"
        className="w-20 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-1.5 py-0.5 text-right text-[color:var(--color-ink)] focus:outline-none focus:border-[color:var(--color-accent)] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <span className="shrink-0">{suffix} / {kr.target ?? '—'}{suffix}</span>
    </span>
  )
}

/** The member's Work Role (designation) label: prefer the renamable catalogue
 *  name, fall back to the fixed tier slug — mirrors `workRoleLabelOf` in the
 *  Space assignee picker so both surfaces show the same designation. */
const WORK_ROLE_TIER_LABEL = { manager: 'Manager', team_leader: 'Team Leader', employee: 'Employee' }
const workRoleLabelOf = (m) =>
  m?.workRoleName ?? WORK_ROLE_TIER_LABEL[m?.workRole] ?? null

/**
 * Searchable avatar people-picker — the same interaction the Space assignee
 * picker (`AssigneeCell` in SpacePage) uses: a search box over a scrollable list
 * of member rows, each an avatar + name you click to toggle, with the selected
 * rows highlighted and a read-only checkbox mirroring state. `multi` false makes
 * it single-select (owner reassign); true toggles a set (followers).
 */
function PeoplePickerList({ items, selectedIds, onToggle, loading = false, emptyText, onQueryChange }) {
  const [q, setQ] = useState('')
  // When `onQueryChange` is supplied the SERVER filters (so it can search the
  // whole roster, not just a capped first page) — render items as-is. Otherwise
  // filter the loaded list client-side.
  const serverMode = typeof onQueryChange === 'function'
  const selected = useMemo(
    () => new Set((selectedIds ?? []).map(String)),
    [selectedIds],
  )
  const filtered = useMemo(() => {
    if (serverMode) return items
    const needle = q.trim().toLowerCase()
    if (!needle) return items
    return items.filter(
      (it) =>
        (it.fullName ?? '').toLowerCase().includes(needle) ||
        (it.email ?? '').toLowerCase().includes(needle) ||
        (it.role ?? '').toLowerCase().includes(needle) ||
        (it.subtitle ?? '').toLowerCase().includes(needle),
    )
  }, [items, q, serverMode])

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)]">
      <div className="border-b border-[color:var(--color-rule)] p-2">
        <div className="relative">
          {/* Leading magnifier INSIDE the pill — matches the shared SearchField
           *  and the rest of the app's search bars. z-10 keeps it above the
           *  pill's backdrop-filter (which would otherwise hide it). */}
          <span
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] pointer-events-none"
          >
            <Search size={14} />
          </span>
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              onQueryChange?.(e.target.value)
            }}
            placeholder="Search by Name or Email ID"
            className="fd-search-pill w-full rounded-full py-1.5 pl-9 pr-3 text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] focus:outline-none"
          />
        </div>
      </div>
      <div className="max-h-52 overflow-auto">
        {loading ? (
          <p className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
            {q ? 'No matches found.' : (emptyText ?? 'No people to show.')}
          </p>
        ) : (
          filtered.map((it) => {
            const isSel = selected.has(String(it.id))
            return (
              <div
                key={it.id}
                role="button"
                tabIndex={0}
                onClick={() => onToggle(it.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggle(it.id)
                  }
                }}
                className={
                  'flex w-full items-center gap-2 px-3 py-2 text-sm cursor-pointer ' +
                  (isSel
                    ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]'
                    : 'hover:bg-[color:var(--color-card-hover)]')
                }
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className="pointer-events-none shrink-0 accent-[color:var(--color-accent)]"
                />
                <Avatar name={it.fullName} size="sm" colorByName tooltip={false} />
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate">{it.fullName}</span>
                  {/* Work Role (designation) as plain muted text + Department as
                   *  a pill — the SAME layout as the Space assignee picker. */}
                  {it.role || it.subtitle ? (
                    <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
                      {it.role ? (
                        <span className="min-w-0 break-all text-xs text-[color:var(--color-ink-muted)]">
                          {it.role}
                        </span>
                      ) : null}
                      {it.subtitle ? (
                        <span className="inline-flex min-w-0 items-center rounded border border-[color:var(--color-rule)] bg-[color:var(--color-card-hover)] px-1.5 py-0.5 text-[10px] font-medium text-[color:var(--color-ink)]">
                          <span className="break-all">{it.subtitle}</span>
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </span>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

/** Owner + follower editor. Both use the same searchable avatar picker as the
 *  Space assignee feature: owner is single-select (reassign), followers are a
 *  multi-select set, saved as the full array via PATCH. */
function ManagePeopleModal({
  open,
  onClose,
  goal,
  onSave,
  saving,
  canReassign = false,
  canRevertReassign = false,
  currentUserId = null,
}) {
  const [followerIds, setFollowerIds] = useState(() => (goal.followers ?? []).map((f) => f.id))
  const [newOwnerId, setNewOwnerId] = useState('')

  // Owner-reassign candidates (only when the viewer may reassign) — the same
  // capability-scoped list the assign flow uses (admins see everyone; a
  // supervisor sees their team). The SERVER searches name/email across the whole
  // roster (the list is capped), so the picker sends the search term rather than
  // filtering a capped first page locally. Server re-validates the chosen owner.
  const [ownerSearch, setOwnerSearch] = useState('')
  const [ownerSearchDebounced, setOwnerSearchDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setOwnerSearchDebounced(ownerSearch.trim()), 250)
    return () => clearTimeout(t)
  }, [ownerSearch])
  const ownersQuery = useQuery({
    queryKey: ['goal-assignable-owners', goal.id, ownerSearchDebounced],
    queryFn: async () =>
      goalAssignableOwnersResponse.parse(
        (
          await api.get('/goals/assignable-owners', {
            params: { q: ownerSearchDebounced || undefined, limit: 25 },
          })
        ).data,
      ),
    enabled: open && canReassign,
  })
  const ownerCandidates = useMemo(
    () => (ownersQuery.data?.data ?? []).filter((u) => String(u.id) !== String(goal.ownerId)),
    [ownersQuery.data, goal.ownerId],
  )

  // Follower candidates — a company-wide actor (admin) can follow anyone in the
  // tenant; others get their department (no rank restriction, so a senior can be
  // followed too). Server-searched by name/email across the (capped) roster, so
  // the picker sends the term rather than filtering a capped first page.
  const [followerSearch, setFollowerSearch] = useState('')
  const [followerSearchDebounced, setFollowerSearchDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setFollowerSearchDebounced(followerSearch.trim()), 250)
    return () => clearTimeout(t)
  }, [followerSearch])
  const followersQuery = useQuery({
    queryKey: ['goal-assignable-followers', goal.id, followerSearchDebounced],
    queryFn: async () =>
      goalAssignableOwnersResponse.parse(
        (
          await api.get('/goals/assignable-followers', {
            params: { q: followerSearchDebounced || undefined, limit: 25 },
          })
        ).data,
      ),
    enabled: open,
  })
  const followerCandidates = useMemo(
    () => (followersQuery.data?.data ?? []).filter((t) => String(t.id) !== String(goal.ownerId)),
    [followersQuery.data, goal.ownerId],
  )
  const toggle = (setFn, list, val) =>
    setFn(list.includes(val) ? list.filter((x) => x !== val) : [...list, val])

  // Map both candidate sources to the shared picker's item shape. `role` is the
  // Work Role (designation) shown under the name, `subtitle` the department —
  // the same pairing the Space assignee picker renders.
  const ownerItems = useMemo(
    () =>
      ownerCandidates.map((u) => ({
        id: u.id,
        fullName: u.fullName,
        email: u.emailId ?? null,
        role: workRoleLabelOf(u),
        subtitle: u.departmentName ?? null,
      })),
    [ownerCandidates],
  )
  const followerItems = useMemo(
    () =>
      followerCandidates.map((t) => ({
        id: t.id,
        fullName: t.fullName,
        email: t.emailId ?? null,
        role: workRoleLabelOf(t),
        subtitle: t.departmentName ?? null,
      })),
    [followerCandidates],
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Manage People"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            loading={saving}
            onClick={() => onSave({ followerIds })}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {canRevertReassign && currentUserId ? (
          <div className="rounded-lg border border-[color:var(--color-warning-border,var(--color-border))] bg-[color:var(--color-warning-soft,var(--color-surface-muted))] p-3">
            <p className="mb-1 text-sm font-semibold text-[color:var(--color-ink-strong)]">
              Recently reassigned away from you
            </p>
            <p className="mb-2 text-xs text-[color:var(--color-ink-muted)]">
              This goal was reassigned to {goal.ownerName ?? '—'}. If that was a mistake, you can take
              the assignment back for a short time.
            </p>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="secondary"
                loading={saving}
                onClick={() => onSave({ ownerId: currentUserId })}
              >
                Take Assignment Back
              </Button>
            </div>
          </div>
        ) : null}
        {canReassign ? (
          <div>
            <p className="mb-1 text-sm font-semibold text-[color:var(--color-ink-strong)]">Assignee</p>
            <p className="mb-2 text-xs text-[color:var(--color-ink-muted)]">
              Currently {goal.ownerName ?? '—'}. Reassigning transfers accountability to the member you pick.
            </p>
            <PeoplePickerList
              items={ownerItems}
              selectedIds={newOwnerId ? [newOwnerId] : []}
              onToggle={(id) =>
                setNewOwnerId((prev) => (String(prev) === String(id) ? '' : String(id)))
              }
              loading={ownersQuery.isLoading}
              emptyText="No members available to reassign to."
              onQueryChange={setOwnerSearch}
            />
            <div className="mt-2 flex justify-end">
              <Button
                type="button"
                variant="secondary"
                loading={saving}
                disabled={!newOwnerId}
                onClick={() => onSave({ ownerId: newOwnerId })}
              >
                Reassign Assignee
              </Button>
            </div>
          </div>
        ) : null}
        <div>
          <p className="mb-2 text-sm font-semibold text-[color:var(--color-ink-strong)]">Followers</p>
          <PeoplePickerList
            items={followerItems}
            selectedIds={followerIds}
            onToggle={(id) => toggle(setFollowerIds, followerIds, id)}
            loading={followersQuery.isLoading}
            emptyText="No members available to add."
            onQueryChange={setFollowerSearch}
          />
        </div>
      </div>
    </Modal>
  )
}
